/**
 * Pure quote-building logic for the sales pricing calculator (PE-2143).
 *
 * Extracted from CalculatorPanel so the two conversions that carry real
 * contract risk can be tested without mounting React:
 *
 *  1. Percent <-> fraction. order-service validates `@Min(0) @Max(1)` on
 *     `commissionPercentage`, so a whole percent (65) is a 400, and a
 *     mis-scaled fraction silently prices the deal 100x wrong.
 *  2. Whether `commissionPercentage` is sent at all. Omitting it makes the
 *     backend apply its configured standard rate; sending the wrong thing
 *     quietly overrides Finance's standard.
 *
 * See types.ts for the full money/percentage contract.
 */
import type {
  SalesCommercialModel,
  SalesPriceTier,
} from './types';
import { SalesCommercialModel as CommercialModel } from './types';

/** The request body the calculator preview / persist endpoints accept. */
export interface QuotePayload {
  addOnFeatureIds: string[];
  commercialModel: SalesCommercialModel;
  chosenTier: SalesPriceTier;
  /** Fraction in 0..1. Absent means "apply the configured standard". */
  commissionPercentage?: number;
}

export interface QuoteInputs {
  addOnFeatureIds: string[];
  commercialModel: SalesCommercialModel;
  chosenTier: SalesPriceTier;
  /** `null` while the configured standard hasn't loaded and the user hasn't typed. */
  commissionPercentage: number | null;
}

/**
 * Renders a stored 0..1 fraction as the whole-percent value shown in the input.
 * Rounds to one decimal place, matching the 0.5 step on the field.
 */
export function fractionToPercentInput(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0;
  return Math.round(fraction * 1000) / 10;
}

/**
 * Converts a typed whole percent back to the 0..1 fraction the backend requires,
 * clamped to the valid range so the field can never emit a value that 400s.
 */
export function percentInputToFraction(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  return Math.min(100, Math.max(0, percent)) / 100;
}

/**
 * Builds the calculator request body.
 *
 * `commissionPercentage` is included only for the commission model and only
 * once a rate actually exists. Note the explicit `!== null`: a negotiated 0%
 * is a real, sendable rate, and a truthiness check here would silently fall
 * back to the configured standard instead.
 */
export function buildQuotePayload({
  addOnFeatureIds,
  commercialModel,
  chosenTier,
  commissionPercentage,
}: QuoteInputs): QuotePayload {
  const isCommission = commercialModel === CommercialModel.COMMISSION_ON_SALES;
  return {
    addOnFeatureIds,
    commercialModel,
    chosenTier,
    ...(isCommission && commissionPercentage !== null ? { commissionPercentage } : {}),
  };
}
